import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, Loader2, Users, Calendar, CheckCircle, XCircle, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { format } from "date-fns";
import { OrgUsersManager } from "./OrgUsersManager";
import { OrgDetailsEditor } from "./OrgDetailsEditor";
import { CreateOrgDialog } from "./CreateOrgDialog";
import { DeleteOrgDialog } from "./DeleteOrgDialog";
import { AISpendingManager } from "./AISpendingManager";

interface Organization {
  id: string;
  name: string;
  slug: string | null;
  plan: string | null;
  pricing_tier_id: string | null;
  subscription_status: string | null;
  elevenlabs_agent_id: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  created_at: string;
  trial_ends_at: string | null;
  pricing_tiers?: {
    tier_name: string;
  } | null;
  _count: {
    jobs: number;
    users: number;
  };
}

export function AccountManagementPanel() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [filteredOrgs, setFilteredOrgs] = useState<Organization[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      setLoading(true);

      // Fetch all organizations with pricing tier info (platform owner bypass allows this)
      const { data: orgs, error: orgsError } = await supabase
        .from('organizations')
        .select(`
          *,
          pricing_tiers (
            tier_name
          )
        `)
        .order('created_at', { ascending: false });

      if (orgsError) throw orgsError;

      // Fetch job counts for each org
      const orgsWithCounts = await Promise.all(
        (orgs || []).map(async (org) => {
          const [jobsCount, usersCount] = await Promise.all([
            supabase
              .from('jobs')
              .select('id', { count: 'exact', head: true })
              .eq('org_id', org.id),
            supabase
              .from('profiles')
              .select('id', { count: 'exact', head: true })
              .eq('org_id', org.id),
          ]);

          return {
            ...org,
            _count: {
              jobs: jobsCount.count || 0,
              users: usersCount.count || 0,
            },
          };
        })
      );

      setOrganizations(orgsWithCounts);
      setFilteredOrgs(orgsWithCounts);
    } catch (error: any) {
      console.error('Failed to load organizations:', error);
      toast.error("Failed to load organizations");
    } finally {
      setLoading(false);
    }
  };

  // Filter organizations based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredOrgs(organizations);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = organizations.filter((org) =>
        org.name.toLowerCase().includes(query) ||
        (org.subscription_status?.toLowerCase() || "").includes(query) ||
        (org.pricing_tiers?.tier_name.toLowerCase() || "").includes(query) ||
        (org.plan?.toLowerCase() || "").includes(query)
      );
      setFilteredOrgs(filtered);
    }
  }, [searchQuery, organizations]);

  const handleViewActivity = (orgId: string) => {
    setSelectedOrgId(orgId);
    // Scroll to activity tab
    const activityTab = document.querySelector('[value="activity"]') as HTMLButtonElement;
    activityTab?.click();
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Organization Management</CardTitle>
              <CardDescription>
                Viewing all {organizations.length} contractor organizations on the platform
              </CardDescription>
            </div>
            <CreateOrgDialog onSuccess={loadOrganizations} />
          </div>
          
          {/* Search Bar */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search organizations by name, tier, or status..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchQuery("")}
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Agent ID</TableHead>
                <TableHead>Users</TableHead>
                <TableHead>Jobs</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrgs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    {searchQuery ? `No organizations found matching "${searchQuery}"` : "No organizations found"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrgs.map((org) => (
                  <TableRow key={org.id}>
                    <TableCell className="font-medium">
                      <div className="flex flex-col">
                        <span>{org.name}</span>
                        {org.slug && (
                          <span className="text-xs text-muted-foreground">{org.slug}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {org.pricing_tiers?.tier_name || org.plan || 'No Tier'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {org.subscription_status === 'active' ? (
                        <Badge variant="default" className="gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Active
                        </Badge>
                      ) : org.subscription_status === 'trial' ? (
                        <Badge variant="secondary" className="gap-1">
                          <Calendar className="w-3 h-3" />
                          Trial
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="gap-1">
                          <XCircle className="w-3 h-3" />
                          {org.subscription_status || 'Inactive'}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-2 py-1 rounded">
                        {org.elevenlabs_agent_id || 'Not set'}
                      </code>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        {org._count.users}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {org._count.jobs}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(org.created_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <OrgDetailsEditor org={org} onUpdate={loadOrganizations} />
                        <OrgUsersManager orgId={org.id} orgName={org.name} />
                        <AISpendingManager orgId={org.id} orgName={org.name} />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleViewActivity(org.id)}
                          className="gap-2"
                        >
                          <Eye className="w-4 h-4" />
                          Activity
                        </Button>
                        <DeleteOrgDialog 
                          orgId={org.id} 
                          orgName={org.name} 
                          onSuccess={loadOrganizations} 
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
